import type { ConditionalOperator, IframeAspectRatio } from "@repo/shared";
import {
  findConditionRuleProblem,
  hasCapacityVariantEnabled,
  IFRAME_HEIGHT_DEFAULT,
  iframeUrlProblem,
  isComponentTypeAllowed,
  isSelfReferentialConditionSource,
  isValidIframeHeight,
  pickT,
  renderedRiskSections,
  resolveRiskInputScopes,
} from "@repo/shared";
import {
  ASSET_CLASS_FORECAST_HORIZONS,
  type AssetClassForecastHorizon,
  type QuizSettings,
  type RiskSection,
} from "@repo/shared/types";
import { useEffect, useMemo, useState } from "react";
import { conditionRuleProblemMessage } from "@/components/conditional-display/condition-rule-problem-message";
import { getFlowComponentLabel } from "@/components/flow/flow-component-types";
import { isAppOrigin } from "@/components/sandboxed-iframe";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError, post } from "@/lib/api/_client";
import { useFlowConfiguredAssessmentTypes } from "@/lib/hooks/use-configured-assessment-types";
import { useFlowQuizzes } from "@/lib/hooks/use-quizzes";
import { useRiskModel } from "@/lib/hooks/use-risk-models";
import type { ComponentStyleType, ComponentType, PortfolioXrayDisplayMode } from "@/lib/types/flow";
import { isFieldAllowedOnPage } from "@/lib/types/flow";
import { SettingsPortfolioDisplayForm } from "../component-settings-dialog/settings-portfolio-display-form";
import { ConditionalVisibilityFields } from "../conditional-visibility-fields";
import { EmbedEditor } from "../pages/detail/component-editors/embed-editor";
import { FileEditor } from "../pages/detail/component-editors/file-editor";
import { IframeEditor } from "../pages/detail/component-editors/iframe-editor";
import { ImageEditor } from "../pages/detail/component-editors/image-editor";
import { ModalEditor } from "../pages/detail/component-editors/modal-editor";
import { YoutubeVideoEditor } from "../pages/detail/component-editors/youtube-video-editor";
import { EMPTY_QUIZ_SETTINGS, QuizSettingsEditor, quizSettingsError } from "../quiz-editor";
import { T } from "./add-component-dialog.i18n";
import type { AddComponentDialogProps } from "./add-component-types";
import { AssetAllocationConfigForm } from "./asset-allocation-config-form";
import { getComponentDefault } from "./component-defaults";
import { ComponentSelector } from "./component-selector";
import { CostChartConfigForm } from "./cost-chart-config-form";
import { CustomPortfolioConfigForm } from "./custom-portfolio-config-form";
import { ExpectedValueConfigForm } from "./expected-value-config-form";
import { FinancialSituationConfigForm } from "./financial-situation-config-form";
import { HorizonChartConfigForm } from "./horizon-chart-config-form";
import { PortfolioForecastConfigForm } from "./portfolio-forecast-config-form";
import { PortfolioInputConfigForm } from "./portfolio-input-config-form";
import { PortfolioListConfigForm } from "./portfolio-list-config-form";
import { StyleConfigForm } from "./style-config-form";
import { SumSubVerificationConfigForm } from "./sumsub-verification-config-form";
import { XRayConfigForm } from "./xray-config-form";

export function AddComponentDialog({
  open,
  pageId,
  flowId,
  flowLanguage,
  pageType = "global",
  portfolioCollectionId = null,
  availableFields,
  clientCustomFields = [],
  targetLayout,
  onClose,
  hasXRayAccess = false,
  hasRiskModel = false,
  riskModelId = null,
  hasPortfolioCollection = false,
  financialSituationModel,
  componentVisibility,
  collectionSettings,
  trapetsMode,
  sumsubMode,
}: AddComponentDialogProps) {
  // Use flow language for all UI in this dialog
  const t = pickT(T, flowLanguage);

  // QL-266 — the capacity section only renders when the model enables the
  // investment capacity calculator for at least one client type.
  const capacityConfiguredInModel = hasCapacityVariantEnabled(financialSituationModel?.settings);

  // QL-13 — the flow's quizzes, so a rule naming a deleted one is caught on
  // save rather than silently hiding content at runtime. While the list is
  // loading it is empty, which would read as "every quiz is gone", so the
  // check waits for it.
  const { quizzes: flowQuizzes, isUnknown: quizzesUnknown } = useFlowQuizzes(flowId);
  const { configuredAssessmentTypes, isUnknown: assessmentTypesUnknown } =
    useFlowConfiguredAssessmentTypes(flowId);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Two-step wizard state
  const [step, setStep] = useState<"select" | "configure">("select");

  // Form state
  const [componentType, setComponentType] = useState<ComponentType>("field");
  const [selectedFieldId, setSelectedFieldId] = useState<string>("");
  // QL-13 — the quiz is authored right here; the component IS the quiz.
  const [quizSettings, setQuizSettings] = useState<QuizSettings>(EMPTY_QUIZ_SETTINGS);
  const [textContent, setTextContent] = useState("");
  const [spacerHeight, setSpacerHeight] = useState("20");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [iframeUrl, setIframeUrl] = useState("");
  const [iframeHeight, setIframeHeight] = useState(String(IFRAME_HEIGHT_DEFAULT));
  const [iframeAspectRatio, setIframeAspectRatio] = useState<"" | IframeAspectRatio>("");
  const [imageUrl, setImageUrl] = useState("");
  const [embedHtml, setEmbedHtml] = useState("");
  const [modalButtonLabel, setModalButtonLabel] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");
  const [fileLabel, setFileLabel] = useState("");
  const [fileS3Key, setFileS3Key] = useState("");
  const [fileFileName, setFileFileName] = useState("");
  const [xrayDisplayMode, setXrayDisplayMode] =
    useState<PortfolioXrayDisplayMode>("asset_allocation");
  const [xrayShowChart, setXrayShowChart] = useState(true);
  const [portfolioDisplayShowChart, setPortfolioDisplayShowChart] = useState(false);
  const [portfolioDisplayShowTable, setPortfolioDisplayShowTable] = useState(true);
  const [portfolioDisplayShowName, setPortfolioDisplayShowName] = useState(true);
  const [xrayShowTable, setXrayShowTable] = useState(true);
  const [assetAllocShowChart, setAssetAllocShowChart] = useState(true);
  const [assetAllocShowTable, setAssetAllocShowTable] = useState(true);
  // QL-315 — asset class forecast
  const [acfShowChart, setAcfShowChart] = useState(true);
  const [acfShowTable, setAcfShowTable] = useState(true);
  const [acfEnabledHorizons, setAcfEnabledHorizons] = useState<AssetClassForecastHorizon[]>([
    ...ASSET_CLASS_FORECAST_HORIZONS,
  ]);
  const [acfDefaultHorizon, setAcfDefaultHorizon] = useState<AssetClassForecastHorizon>("10Y");
  // QL-314 — volatility chart
  const [vcEnabledHorizons, setVcEnabledHorizons] = useState<AssetClassForecastHorizon[]>([
    ...ASSET_CLASS_FORECAST_HORIZONS,
  ]);
  const [vcDefaultHorizon, setVcDefaultHorizon] = useState<AssetClassForecastHorizon>("1Y");
  const [vcShowTable, setVcShowTable] = useState(false);
  // QL-313 — equity share chart
  const [escEnabledHorizons, setEscEnabledHorizons] = useState<AssetClassForecastHorizon[]>([
    ...ASSET_CLASS_FORECAST_HORIZONS,
  ]);
  const [escDefaultHorizon, setEscDefaultHorizon] = useState<AssetClassForecastHorizon>("1Y");
  const [escShowTable, setEscShowTable] = useState(true);
  const [expectedValueShowChart, setExpectedValueShowChart] = useState(true);
  const [expectedValueShowSummaryTable, setExpectedValueShowSummaryTable] = useState(false);
  const [expectedValueShowYearlyTable, setExpectedValueShowYearlyTable] = useState(false);
  const [forecastShowStatistics, setForecastShowStatistics] = useState(true);
  const [forecastShowTable, setForecastShowTable] = useState(true);
  const [costChartDisplayMode, setCostChartDisplayMode] = useState<"rolling" | "calendar">(
    "rolling",
  );
  const [costChartType, setCostChartType] = useState<"line" | "bar">("line");
  const [portfolioInputAllowedPortfolioTypes, setPortfolioInputAllowedPortfolioTypes] = useState<
    ("model" | "custom" | "dynamic")[]
  >(["model", "custom", "dynamic"]);
  // QL-697 — undefined = automatic (model when allowed, else the first allowed type).
  const [portfolioInputDefaultPortfolioType, setPortfolioInputDefaultPortfolioType] = useState<
    "model" | "custom" | "dynamic" | undefined
  >(undefined);
  const [portfolioInputShowPortfolioName, setPortfolioInputShowPortfolioName] = useState(true);
  const [portfolioInputShowDateInputs, setPortfolioInputShowDateInputs] = useState(false);
  // Product Assessment / Client Classification settings
  const [componentStyle, setComponentStyle] = useState<ComponentStyleType>("none");
  const [componentTitle, setComponentTitle] = useState("");
  const [componentDescription, setComponentDescription] = useState("");
  // Custom Portfolio field visibility
  const [customPortfolioShowIsin, setCustomPortfolioShowIsin] = useState(false);
  const [customPortfolioShowProductType, setCustomPortfolioShowProductType] = useState(false);
  // Financial Situation config
  const [finSitShowIncome, setFinSitShowIncome] = useState(true);
  const [finSitShowAssets, setFinSitShowAssets] = useState(true);
  const [finSitShowLiabilities, setFinSitShowLiabilities] = useState(true);
  const [finSitShowCommitments, setFinSitShowCommitments] = useState(true);
  const [finSitShowCapacitySection, setFinSitShowCapacitySection] = useState(true);
  const [finSitShowStability, setFinSitShowStability] = useState(true);
  const [finSitShowExpectingChanges, setFinSitShowExpectingChanges] = useState(true);
  const [finSitShowCapacity, setFinSitShowCapacity] = useState(true);
  const [finSitShowSummary, setFinSitShowSummary] = useState(true);
  const [sumsubLivenessVerification, setSumsubLivenessVerification] = useState(false);
  // QL-281 — Goal List: show the description textarea in the add/edit modal
  const [goalListShowDescription, _setGoalListShowDescription] = useState(false);
  // Portfolio List payment-detail visibility
  const [portfolioListShowAmount, setPortfolioListShowAmount] = useState(true);
  const [portfolioListShowFrequency, setPortfolioListShowFrequency] = useState(true);
  const [portfolioListShowDates, setPortfolioListShowDates] = useState(true);
  // Conditional display config (mirrors the post-create component settings dialog)
  const [conditionalEnabled, setConditionalEnabled] = useState(false);
  const [sourceFieldId, setSourceFieldId] = useState("");
  const [operator, setOperator] = useState<ConditionalOperator>("equals");
  const [conditionValue, setConditionValue] = useState("");

  // Filter available fields based on scope and page type
  const filteredFields = useMemo(() => {
    return availableFields.filter((field) =>
      isFieldAllowedOnPage(field.scope || "global", pageType),
    );
  }, [availableFields, pageType]);

  // Sources the component being added itself produces (own flow field /
  // produced sentinels) — removed from the condition-source picker; a
  // self-referential rule would deadlock or trap-door the component (the
  // server rejects it too). Reactive: changing the picked field re-filters.
  // QL-361: no own component id here — a component being added has none yet,
  // so a new quiz sees every quiz; a new product assessment still sees no
  // product-assessment source (that rule is by prefix, not by id).
  const isConditionSourceExcluded = (sourceId: string) =>
    isSelfReferentialConditionSource(componentType, sourceId, selectedFieldId || null);

  useEffect(() => {
    if (open) {
      // Reset form
      setStep("select");
      setComponentType("field");
      setSelectedFieldId("");
      // QL-13 — the dialog stays mounted between opens, so an authored quiz
      // would otherwise be carried into the next component added.
      setQuizSettings(EMPTY_QUIZ_SETTINGS);
      setTextContent("");
      setSpacerHeight("20");
      setYoutubeUrl("");
      setIframeUrl("");
      setIframeHeight(String(IFRAME_HEIGHT_DEFAULT));
      setIframeAspectRatio("");
      setImageUrl("");
      setEmbedHtml("");
      setModalButtonLabel("");
      setModalTitle("");
      setModalContent("");
      setFileLabel("");
      setFileS3Key("");
      setFileFileName("");
      setXrayDisplayMode("asset_allocation");
      setXrayShowChart(true);
      setXrayShowTable(true);
      setPortfolioDisplayShowChart(false);
      setPortfolioDisplayShowTable(true);
      setPortfolioDisplayShowName(true);
      setAssetAllocShowChart(true);
      setAssetAllocShowTable(true);
      setAcfShowChart(true);
      setAcfShowTable(true);
      setAcfDefaultHorizon("10Y");
      setVcEnabledHorizons([...ASSET_CLASS_FORECAST_HORIZONS]);
      setVcDefaultHorizon("1Y");
      setVcShowTable(false);
      // QL-313 — the other two horizon charts' settings reset with the rest.
      setAcfEnabledHorizons([...ASSET_CLASS_FORECAST_HORIZONS]);
      setEscEnabledHorizons([...ASSET_CLASS_FORECAST_HORIZONS]);
      setEscDefaultHorizon("1Y");
      setEscShowTable(true);
      setExpectedValueShowChart(true);
      setExpectedValueShowSummaryTable(false);
      setExpectedValueShowYearlyTable(false);
      setForecastShowStatistics(true);
      setForecastShowTable(true);
      setCostChartDisplayMode("rolling");
      setPortfolioInputShowPortfolioName(true);
      setPortfolioInputShowDateInputs(false);
      setPortfolioInputAllowedPortfolioTypes(["model", "custom", "dynamic"]);
      setPortfolioInputDefaultPortfolioType(undefined);
      setComponentStyle("none");
      setComponentTitle("");
      setComponentDescription("");
      setCustomPortfolioShowIsin(false);
      setCustomPortfolioShowProductType(false);
      setFinSitShowIncome(true);
      setFinSitShowAssets(true);
      setFinSitShowLiabilities(true);
      setFinSitShowCommitments(true);
      setFinSitShowStability(true);
      setFinSitShowCapacitySection(true);
      setFinSitShowExpectingChanges(true);
      setFinSitShowCapacity(true);
      setFinSitShowSummary(true);
      setSumsubLivenessVerification(false);
      setPortfolioListShowAmount(true);
      setPortfolioListShowFrequency(true);
      setPortfolioListShowDates(true);
      setConditionalEnabled(false);
      setSourceFieldId("");
      setOperator("equals");
      setConditionValue("");
      setError(null);
      setLoading(false);
    }
  }, [open]);

  // Clear the compare value when the source field changes (mirrors the settings dialog;
  // different fields have different valid values)
  const handleSourceFieldChange = (newFieldId: string) => {
    setSourceFieldId(newFieldId);
    setConditionValue("");
  };

  const handleComponentTypeChange = (type: ComponentType) => {
    setComponentType(type);
    // Set default title/description/style for specific component types
    const defaults = getComponentDefault(flowLanguage, type);
    if (defaults) {
      // Components with localized defaults
      setComponentStyle("border");
      setComponentTitle(defaults.title);
      setComponentDescription(defaults.description);
    } else {
      // Reset for other types (text_block, spacer, youtube_video, image, embed, field)
      setComponentStyle("none");
      setComponentTitle("");
      setComponentDescription("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (componentType === "field" && !selectedFieldId) {
      setError(t.pleaseSelectField);
      return;
    }

    if (componentType === "quiz") {
      const quizError = quizSettingsError(quizSettings, flowLanguage);
      if (quizError) {
        setError(quizError);
        return;
      }
    }

    if (componentType === "text_block" && !textContent.trim()) {
      setError(t.pleaseEnterTextContent);
      return;
    }

    if (componentType === "spacer") {
      const height = parseInt(spacerHeight, 10);
      if (Number.isNaN(height) || height < 1 || height > 500) {
        setError(t.heightMustBeBetween);
        return;
      }
    }

    if (componentType === "youtube_video" && !youtubeUrl.trim()) {
      setError(t.pleaseEnterYoutubeUrl);
      return;
    }

    if (componentType === "iframe") {
      if (iframeUrlProblem(iframeUrl.trim()) !== null) {
        setError(t.iframeUrlInvalid);
        return;
      }
      if (isAppOrigin(iframeUrl.trim())) {
        setError(t.iframeUrlAppOrigin);
        return;
      }
      if (!iframeAspectRatio && !isValidIframeHeight(Number(iframeHeight))) {
        setError(t.iframeHeightInvalid);
        return;
      }
    }

    if (componentType === "image" && !imageUrl.trim()) {
      setError(t.pleaseUploadImage);
      return;
    }

    if (componentType === "embed" && !embedHtml.trim()) {
      setError(t.pleaseEnterEmbedCode);
      return;
    }

    if (componentType === "modal") {
      if (!modalButtonLabel.trim()) {
        setError(t.pleaseEnterModalButtonLabel);
        return;
      }
      if (!modalTitle.trim()) {
        setError(t.pleaseEnterModalTitle);
        return;
      }
      if (!modalContent.trim()) {
        setError(t.pleaseEnterModalContent);
        return;
      }
    }

    if (componentType === "file" && !fileS3Key) {
      setError(t.pleaseUploadDocument);
      return;
    }

    if (
      componentType === "portfolio_xray" &&
      xrayDisplayMode !== "top_holdings" &&
      xrayDisplayMode !== "equity_style" &&
      !xrayShowChart &&
      !xrayShowTable
    ) {
      setError(t.pleaseSelectDisplayOption);
      return;
    }

    if (
      componentType === "portfolio_display" &&
      !portfolioDisplayShowChart &&
      !portfolioDisplayShowTable
    ) {
      setError(t.pleaseSelectDisplayOption);
      return;
    }

    if (componentType === "asset_allocation" && !assetAllocShowChart && !assetAllocShowTable) {
      setError(t.pleaseSelectDisplayOption);
      return;
    }

    if (componentType === "asset_class_forecast" && !acfShowChart && !acfShowTable) {
      setError(t.pleaseSelectDisplayOption);
      return;
    }

    // QL-313 — the horizon-chart settings block lets every pill be unpressed
    // (with a warning) rather than disabling the last one; the save refuses it.
    if (
      (componentType === "asset_class_forecast" && acfEnabledHorizons.length === 0) ||
      (componentType === "volatility_chart" && vcEnabledHorizons.length === 0) ||
      (componentType === "equity_share_chart" && escEnabledHorizons.length === 0)
    ) {
      setError(t.pleaseSelectTimeRange);
      return;
    }

    if (
      componentType === "expected_value" &&
      !expectedValueShowChart &&
      !expectedValueShowSummaryTable &&
      !expectedValueShowYearlyTable
    ) {
      setError(t.pleaseSelectOneDisplayOption);
      return;
    }

    if (componentType === "portfolio_input" && portfolioInputAllowedPortfolioTypes.length === 0) {
      setError(t.pleaseSelectPortfolioType);
      return;
    }

    // Conditional display validation (mirrors the post-create component settings dialog)
    const ruleProblem = conditionalEnabled
      ? findConditionRuleProblem(
          { sourceFieldId, operator, value: conditionValue },
          {
            quizzes: quizzesUnknown ? null : flowQuizzes,
            assessmentTypes: assessmentTypesUnknown ? null : configuredAssessmentTypes,
          },
        )
      : null;
    if (ruleProblem) {
      setError(conditionRuleProblemMessage(ruleProblem, flowLanguage));
      return;
    }

    setLoading(true);

    try {
      // Build config based on component type
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let config: any = null;

      if (componentType === "text_block") {
        config = { content: textContent };
      } else if (componentType === "spacer") {
        config = { height: parseInt(spacerHeight, 10) };
      } else if (componentType === "youtube_video") {
        config = { url: youtubeUrl };
      } else if (componentType === "iframe") {
        config = {
          url: iframeUrl.trim(),
          ...(iframeAspectRatio
            ? { aspectRatio: iframeAspectRatio }
            : { height: Number(iframeHeight) }),
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
        };
      } else if (componentType === "image") {
        config = { url: imageUrl };
      } else if (componentType === "embed") {
        config = {
          html: embedHtml,
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
        };
      } else if (componentType === "modal") {
        config = {
          buttonLabel: modalButtonLabel.trim(),
          title: modalTitle.trim(),
          content: modalContent,
        };
      } else if (componentType === "file") {
        config = {
          label: fileLabel.trim() || undefined,
          s3Key: fileS3Key,
          fileName: fileFileName,
        };
      } else if (componentType === "portfolio_xray") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          displayMode: xrayDisplayMode,
          showChart: xrayShowChart,
          showTable: xrayShowTable,
        };
      } else if (componentType === "portfolio_forecast") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          showStatistics: forecastShowStatistics,
          showTable: forecastShowTable,
        };
      } else if (componentType === "asset_allocation") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          showChart: assetAllocShowChart,
          showTable: assetAllocShowTable,
        };
      } else if (componentType === "asset_class_forecast") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          showChart: acfShowChart,
          showTable: acfShowTable,
          enabledHorizons: acfEnabledHorizons,
          defaultHorizon: acfDefaultHorizon,
        };
      } else if (componentType === "volatility_chart") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          enabledHorizons: vcEnabledHorizons,
          defaultHorizon: vcDefaultHorizon,
          showTable: vcShowTable,
        };
      } else if (componentType === "equity_share_chart") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          enabledHorizons: escEnabledHorizons,
          defaultHorizon: escDefaultHorizon,
          showTable: escShowTable,
        };
      } else if (componentType === "portfolio_display") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          showChart: portfolioDisplayShowChart,
          showTable: portfolioDisplayShowTable,
          showPortfolioName: portfolioDisplayShowName,
        };
      } else if (componentType === "portfolio_input") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          showPortfolioType: portfolioInputAllowedPortfolioTypes.length > 0,
          allowedPortfolioTypes: portfolioInputAllowedPortfolioTypes,
          defaultPortfolioType: portfolioInputDefaultPortfolioType,
          showPortfolioName: portfolioInputShowPortfolioName,
          showDateInputs: portfolioInputShowDateInputs,
        };
      } else if (componentType === "goal_input" || componentType === "risk_assessment") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
        };
      } else if (componentType === "expected_value") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          showChart: expectedValueShowChart,
          showSummaryTable: expectedValueShowSummaryTable,
          showYearlyTable: expectedValueShowYearlyTable,
        };
      } else if (componentType === "historical_return") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
        };
      } else if (
        componentType === "product_assessment" ||
        componentType === "client_classification"
      ) {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
        };
      } else if (componentType === "custom_portfolio") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          showIsin: customPortfolioShowIsin,
          showProductType: customPortfolioShowProductType,
        };
      } else if (componentType === "cost_table") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
        };
      } else if (componentType === "cost_chart") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          displayMode: costChartDisplayMode,
          chartType: costChartType,
        };
      } else if (componentType === "portfolio_costs") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
        };
      } else if (
        componentType === "product_costs" ||
        componentType === "cost_benefit" ||
        componentType === "account_cash_overview" ||
        componentType === "cash_transfers" ||
        // QL-375 — title / description / style only.
        componentType === "mandate" ||
        componentType === "mandate_comparison"
      ) {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
        };
      } else if (componentType === "wealth_plan") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
        };
      } else if (componentType === "goal_list") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          showDescription: goalListShowDescription,
        };
      } else if (
        componentType === "sustainability" ||
        componentType === "sustainability_status" ||
        componentType === "client_preferences"
      ) {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
        };
      } else if (componentType === "quiz") {
        config = {
          ...quizSettings,
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
        };
      } else if (componentType === "financial_situation") {
        // Parse field definitions from the assigned financial situation model
        const parseFields = (json: string | undefined) => {
          if (!json) return undefined;
          try {
            const parsed = JSON.parse(json);
            return parsed.length > 0 ? parsed : undefined;
          } catch {
            return undefined;
          }
        };

        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          // Field definitions from the financial situation model
          incomeFields: parseFields(financialSituationModel?.incomeFields),
          assetFields: parseFields(financialSituationModel?.assetFields),
          liabilityFields: parseFields(financialSituationModel?.liabilityFields),
          commitmentFields: parseFields(financialSituationModel?.commitmentFields),
          // Section visibility (can split across pages)
          showIncome: finSitShowIncome,
          showAssets: finSitShowAssets,
          showLiabilities: finSitShowLiabilities,
          showCommitments: finSitShowCommitments,
          showCapacity: finSitShowCapacitySection,
          // Display options
          showStabilityQuestion: finSitShowStability,
          showExpectingChangesQuestion: finSitShowExpectingChanges,
          showCapacityClassification: finSitShowCapacity,
          showSummaryMetrics: finSitShowSummary,
        };
      } else if (componentType === "trapets_lookup") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
        };
      } else if (componentType === "sumsub_verification") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          livenessVerification: sumsubLivenessVerification,
        };
      } else if (componentType === "enin_lookup") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
        };
      } else if (componentType === "portfolio_list") {
        config = {
          style: componentStyle,
          title: componentTitle.trim() || undefined,
          description: componentDescription.trim() || undefined,
          showAmount: portfolioListShowAmount,
          showFrequency: portfolioListShowFrequency,
          showDates: portfolioListShowDates,
        };
      }

      // Attach conditional display rule (config may be null for some component types, e.g. field)
      if (conditionalEnabled) {
        config = config ?? {};
        config.conditionalDisplay = {
          enabled: true,
          sourceFieldId,
          operator,
          value: conditionValue,
        };
      }

      await post(`/internal/flows/${flowId}/pages/${pageId}/components`, {
        componentType,
        flowFieldId: componentType === "field" ? selectedFieldId : undefined,
        config,
        flowPageLayoutId: targetLayout?.layoutId || null,
        columnPosition: targetLayout?.column || null,
        tabId: targetLayout?.tabId || null,
      });

      onClose(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "An unexpected error occurred");
      setLoading(false);
    }
  };

  // Check if component needs configuration step
  const componentNeedsConfiguration = (type: ComponentType): boolean => {
    // Components that have configuration options
    const configurable = [
      "field",
      "text_block",
      "spacer",
      "youtube_video",
      "iframe",
      "image",
      "embed",
      "modal",
      "file",
      "portfolio_xray",
      "asset_allocation",
      "asset_class_forecast",
      "volatility_chart",
      "equity_share_chart",
      "mandate",
      "mandate_comparison",
      "portfolio_forecast",
      "expected_value",
      "cost_chart",
      "goal_input",
      "portfolio_input",
      "custom_portfolio",
      "financial_situation",
      // These have style/title/description options
      "risk_assessment",
      "portfolio_display",
      "historical_return",
      "cost_table",
      "portfolio_costs",
      "product_costs",
      "cost_benefit",
      "client_classification",
      "product_assessment",
      "wealth_plan",
      "trapets_lookup",
      "sumsub_verification",
      "enin_lookup",
      "sustainability",
      "sustainability_status",
      "client_preferences",
      // QL-13 — an empty quiz asks nothing, so step 2 is where it gets built.
      "quiz",
      "portfolio_list",
      "goal_list",
      "account_cash_overview",
      "cash_transfers",
      "mandate",
      "mandate_comparison",
    ];
    return configurable.includes(type);
  };

  // QL-212 — which of the risk assessment's four sections this page would show,
  // for THIS flow's risk model. `null` while the model is still loading (or when
  // the flow has none): an unloaded model normalises to all-per-goal, which on a
  // global page would read as "renders nothing" and wrongly block the Add button.
  const { model: riskModel } = useRiskModel(riskModelId ?? null);
  const riskSections: ReadonlySet<RiskSection> | null = useMemo(
    () => (riskModel ? renderedRiskSections(resolveRiskInputScopes(riskModel), pageType) : null),
    [riskModel, pageType],
  );

  // A requirement-governed component is shown in the picker but cannot be added while its
  // governing collection setting is off (the server-side guard rejects it regardless).
  // PEP/Sanction Lookup is likewise blocked when the tenant's Trapets mode is disabled.
  const selectedBlocked =
    (!!collectionSettings && !isComponentTypeAllowed(collectionSettings, componentType)) ||
    (componentType === "trapets_lookup" && trapetsMode === "disabled") ||
    (componentType === "sumsub_verification" && sumsubMode === "disabled") ||
    (componentType === "risk_assessment" && riskSections?.size === 0);

  const handleNext = () => {
    if (selectedBlocked) return;
    if (componentNeedsConfiguration(componentType)) {
      setStep("configure");
    } else {
      // Submit directly for components with no configuration
      handleSubmit({ preventDefault: () => {} } as React.FormEvent);
    }
  };

  const handleBack = () => {
    setStep("select");
    setError(null);
  };

  // Check if current component type has style/title/description settings
  const hasStyleSettings =
    componentType === "iframe" ||
    componentType === "product_assessment" ||
    componentType === "client_classification" ||
    componentType === "risk_assessment" ||
    componentType === "goal_input" ||
    componentType === "portfolio_input" ||
    componentType === "custom_portfolio" ||
    componentType === "portfolio_display" ||
    componentType === "portfolio_xray" ||
    componentType === "portfolio_forecast" ||
    componentType === "asset_allocation" ||
    componentType === "asset_class_forecast" ||
    componentType === "volatility_chart" ||
    componentType === "equity_share_chart" ||
    componentType === "mandate" ||
    componentType === "mandate_comparison" ||
    componentType === "expected_value" ||
    componentType === "historical_return" ||
    componentType === "cost_table" ||
    componentType === "cost_chart" ||
    componentType === "portfolio_costs" ||
    componentType === "product_costs" ||
    componentType === "cost_benefit" ||
    componentType === "wealth_plan" ||
    componentType === "financial_situation" ||
    componentType === "trapets_lookup" ||
    componentType === "sumsub_verification" ||
    componentType === "enin_lookup" ||
    componentType === "sustainability" ||
    componentType === "sustainability_status" ||
    componentType === "client_preferences" ||
    componentType === "quiz" ||
    componentType === "portfolio_list" ||
    componentType === "goal_list" ||
    componentType === "account_cash_overview" ||
    componentType === "cash_transfers" ||
    componentType === "embed";

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      {/* Same scroll shell as the component settings dialog: the header and the
          footer stay put and only the body scrolls. `DialogContent` already
          scrolls itself, so a second `overflow-y-auto` inside it gave this
          dialog two nested scrollbars. */}
      <DialogScrollContent className={step === "select" ? "max-w-4xl" : "max-w-2xl"}>
        <DialogScrollHeader>
          <DialogTitle>
            {step === "select" ? (
              <>
                {t.addComponent}{" "}
                {targetLayout
                  ? targetLayout.column === "left"
                    ? t.toLeftColumn
                    : t.toRightColumn
                  : t.toPage}
              </>
            ) : (
              <>
                {t.configure} {getFlowComponentLabel(componentType, flowLanguage)}
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {step === "select" ? t.selectComponentType : t.configureSettings}
          </DialogDescription>
        </DialogScrollHeader>

        {step === "select" ? (
          // Step 1: Select component type
          <>
            <DialogBody>
              <ComponentSelector
                flowLanguage={flowLanguage}
                pageType={pageType}
                pageVisibility={componentVisibility}
                hasXRayAccess={hasXRayAccess}
                hasRiskModel={hasRiskModel}
                riskSections={riskSections}
                hasPortfolioCollection={hasPortfolioCollection}
                hasFinancialSituationModel={!!financialSituationModel}
                collectionSettings={collectionSettings}
                trapetsMode={trapetsMode}
                sumsubMode={sumsubMode}
                selectedType={componentType}
                onSelect={handleComponentTypeChange}
              />
            </DialogBody>

            <DialogScrollFooter className="sm:justify-between">
              <DialogCancel>{t.cancel}</DialogCancel>
              <Button type="button" onClick={handleNext} disabled={selectedBlocked}>
                {componentNeedsConfiguration(componentType) ? t.next : t.addComponentBtn}
              </Button>
            </DialogScrollFooter>
          </>
        ) : (
          // Step 2: Configure component
          <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
            <DialogBody className="space-y-4">
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  {error}
                </div>
              )}

              {/* One tab bar for the whole step, matching the edit dialog:
                  what the component is on the left, when it shows on the right.
                  Embed keeps its HTML tab in the middle rather than nesting a
                  second tab bar inside the first. */}
              <Tabs
                defaultValue={componentType === "embed" ? "html" : "component"}
                className="w-full"
              >
                <TabsList
                  className={`grid w-full ${
                    componentType === "embed" ? "grid-cols-3" : "grid-cols-2"
                  }`}
                >
                  <TabsTrigger value="component">{t.componentSettingsTab}</TabsTrigger>
                  {componentType === "embed" && <TabsTrigger value="html">{t.htmlTab}</TabsTrigger>}
                  <TabsTrigger value="conditional">{t.conditionalDisplayTab}</TabsTrigger>
                </TabsList>

                <TabsContent value="component" className="space-y-4 mt-4">
                  {/* Field Selector (only for field component type) */}
                  {componentType === "field" && (
                    <div className="space-y-2">
                      <Label htmlFor="field">
                        {t.selectField} <span className="text-destructive">*</span>
                      </Label>
                      {filteredFields.length === 0 ? (
                        <div className="text-sm text-muted-foreground p-3 border rounded-md">
                          {availableFields.length === 0
                            ? t.noFieldsAvailable
                            : t.noFieldsForPageType}
                        </div>
                      ) : (
                        <>
                          <Select value={selectedFieldId} onValueChange={setSelectedFieldId}>
                            <SelectTrigger id="field">
                              <SelectValue placeholder={t.chooseField} />
                            </SelectTrigger>
                            <SelectContent>
                              {filteredFields.map((field) => (
                                <SelectItem key={field.id} value={field.id}>
                                  {field.name} ({field.type}){field.scope === "goal" && " [Goal]"}
                                  {field.scope === "portfolio" && " [Portfolio]"}
                                  {field.isRequired && " *"}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">{t.chooseFieldHint}</p>
                        </>
                      )}
                    </div>
                  )}

                  {/* Text Content (only for text_block) — QL-296: one Content field,
                      the heading lives inside it (single heading style + paragraphs). */}
                  {componentType === "text_block" && (
                    <div className="space-y-2">
                      <Label htmlFor="textContent">
                        {t.content} <span className="text-destructive">*</span>
                      </Label>
                      <MarkdownEditor
                        value={textContent}
                        onChange={setTextContent}
                        placeholder={t.textPlaceholder}
                        showAI={false}
                        headings="single"
                      />
                      <p className="text-xs text-muted-foreground">{t.textHint}</p>
                    </div>
                  )}

                  {componentType === "quiz" && (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">{t.quizSetupHint}</p>
                      <QuizSettingsEditor
                        value={quizSettings}
                        onChange={setQuizSettings}
                        language={flowLanguage}
                      />
                    </div>
                  )}

                  {/* Spacer Height (only for spacer) */}
                  {componentType === "spacer" && (
                    <div className="space-y-2">
                      <Label htmlFor="spacerHeight">
                        {t.heightPixels} <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="spacerHeight"
                        type="number"
                        min="1"
                        max="500"
                        value={spacerHeight}
                        onChange={(e) => setSpacerHeight(e.target.value)}
                        placeholder="20"
                      />
                      <p className="text-xs text-muted-foreground">{t.heightHint}</p>
                    </div>
                  )}

                  {/* YouTube Video URL (only for youtube_video) */}
                  {componentType === "youtube_video" && (
                    <YoutubeVideoEditor
                      url={youtubeUrl}
                      flowLanguage={flowLanguage}
                      onChange={setYoutubeUrl}
                    />
                  )}

                  {/* Web page URL (only for iframe) */}
                  {componentType === "iframe" && (
                    <IframeEditor
                      url={iframeUrl}
                      height={iframeHeight}
                      aspectRatio={iframeAspectRatio}
                      flowLanguage={flowLanguage}
                      onUrlChange={setIframeUrl}
                      onHeightChange={setIframeHeight}
                      onAspectRatioChange={setIframeAspectRatio}
                    />
                  )}

                  {/* Image Upload (only for image) */}
                  {componentType === "image" && (
                    <ImageEditor
                      url={imageUrl}
                      flowLanguage={flowLanguage}
                      onChange={setImageUrl}
                    />
                  )}

                  {/* Embed's framing lives under Component Settings; its HTML has a
                  tab of its own below. */}
                  {componentType === "embed" && (
                    <StyleConfigForm
                      flowLanguage={flowLanguage}
                      style={componentStyle}
                      onStyleChange={setComponentStyle}
                      title={componentTitle}
                      onTitleChange={setComponentTitle}
                      description={componentDescription}
                      onDescriptionChange={setComponentDescription}
                    />
                  )}

                  {/* Info Modal (only for modal) */}
                  {componentType === "modal" && (
                    <ModalEditor
                      buttonLabel={modalButtonLabel}
                      title={modalTitle}
                      content={modalContent}
                      flowLanguage={flowLanguage}
                      onButtonLabelChange={setModalButtonLabel}
                      onTitleChange={setModalTitle}
                      onContentChange={setModalContent}
                    />
                  )}

                  {/* File Download (only for file) */}
                  {componentType === "file" && (
                    <FileEditor
                      label={fileLabel}
                      fileName={fileFileName}
                      flowLanguage={flowLanguage}
                      onLabelChange={setFileLabel}
                      onFileChange={(s3Key, fileName) => {
                        setFileS3Key(s3Key);
                        setFileFileName(fileName);
                      }}
                    />
                  )}

                  {componentType === "portfolio_display" && (
                    <SettingsPortfolioDisplayForm
                      flowLanguage={flowLanguage}
                      showChart={portfolioDisplayShowChart}
                      onShowChartChange={setPortfolioDisplayShowChart}
                      showTable={portfolioDisplayShowTable}
                      onShowTableChange={setPortfolioDisplayShowTable}
                      showPortfolioName={portfolioDisplayShowName}
                      onShowPortfolioNameChange={setPortfolioDisplayShowName}
                    />
                  )}

                  {/* Portfolio X-Ray Settings */}
                  {componentType === "portfolio_xray" && (
                    <XRayConfigForm
                      flowLanguage={flowLanguage}
                      displayMode={xrayDisplayMode}
                      onDisplayModeChange={setXrayDisplayMode}
                      showChart={xrayShowChart}
                      onShowChartChange={setXrayShowChart}
                      showTable={xrayShowTable}
                      onShowTableChange={setXrayShowTable}
                    />
                  )}

                  {/* Asset Allocation Configuration */}
                  {componentType === "asset_allocation" && (
                    <AssetAllocationConfigForm
                      flowLanguage={flowLanguage}
                      showChart={assetAllocShowChart}
                      onShowChartChange={setAssetAllocShowChart}
                      showTable={assetAllocShowTable}
                      onShowTableChange={setAssetAllocShowTable}
                    />
                  )}

                  {/* Asset Class Forecast Configuration (QL-315) */}
                  {componentType === "asset_class_forecast" && (
                    <HorizonChartConfigForm
                      idPrefix="acf"
                      chart="asset_class_forecast"
                      flowLanguage={flowLanguage}
                      showChart={acfShowChart}
                      onShowChartChange={setAcfShowChart}
                      showTable={acfShowTable}
                      onShowTableChange={setAcfShowTable}
                      enabledHorizons={acfEnabledHorizons}
                      onEnabledHorizonsChange={setAcfEnabledHorizons}
                      defaultHorizon={acfDefaultHorizon}
                      onDefaultHorizonChange={setAcfDefaultHorizon}
                    />
                  )}

                  {/* Volatility Chart Configuration (QL-314) */}
                  {componentType === "volatility_chart" && (
                    <HorizonChartConfigForm
                      idPrefix="vc"
                      chart="volatility_chart"
                      flowLanguage={flowLanguage}
                      enabledHorizons={vcEnabledHorizons}
                      onEnabledHorizonsChange={setVcEnabledHorizons}
                      defaultHorizon={vcDefaultHorizon}
                      onDefaultHorizonChange={setVcDefaultHorizon}
                      showTable={vcShowTable}
                      onShowTableChange={setVcShowTable}
                    />
                  )}

                  {/* Equity Share Chart Configuration (QL-313) */}
                  {componentType === "equity_share_chart" && (
                    <HorizonChartConfigForm
                      idPrefix="esc"
                      chart="equity_share_chart"
                      flowLanguage={flowLanguage}
                      enabledHorizons={escEnabledHorizons}
                      onEnabledHorizonsChange={setEscEnabledHorizons}
                      defaultHorizon={escDefaultHorizon}
                      onDefaultHorizonChange={setEscDefaultHorizon}
                      showTable={escShowTable}
                      onShowTableChange={setEscShowTable}
                    />
                  )}

                  {/* Expected Value Configuration */}
                  {componentType === "expected_value" && (
                    <ExpectedValueConfigForm
                      flowLanguage={flowLanguage}
                      showChart={expectedValueShowChart}
                      onShowChartChange={setExpectedValueShowChart}
                      showSummaryTable={expectedValueShowSummaryTable}
                      onShowSummaryTableChange={setExpectedValueShowSummaryTable}
                      showYearlyTable={expectedValueShowYearlyTable}
                      onShowYearlyTableChange={setExpectedValueShowYearlyTable}
                    />
                  )}

                  {/* Portfolio Forecast Configuration */}
                  {componentType === "portfolio_forecast" && (
                    <PortfolioForecastConfigForm
                      flowLanguage={flowLanguage}
                      showStatistics={forecastShowStatistics}
                      onShowStatisticsChange={setForecastShowStatistics}
                      showTable={forecastShowTable}
                      onShowTableChange={setForecastShowTable}
                    />
                  )}

                  {/* Cost Chart Configuration */}
                  {componentType === "cost_chart" && (
                    <CostChartConfigForm
                      flowLanguage={flowLanguage}
                      displayMode={costChartDisplayMode}
                      onDisplayModeChange={setCostChartDisplayMode}
                      chartType={costChartType}
                      onChartTypeChange={setCostChartType}
                    />
                  )}

                  {/* Portfolio Input Configuration (schedule/amount + model/custom toggle) */}
                  {componentType === "portfolio_input" && (
                    <PortfolioInputConfigForm
                      flowLanguage={flowLanguage}
                      allowedPortfolioTypes={portfolioInputAllowedPortfolioTypes}
                      onAllowedPortfolioTypesChange={setPortfolioInputAllowedPortfolioTypes}
                      defaultPortfolioType={portfolioInputDefaultPortfolioType}
                      onDefaultPortfolioTypeChange={setPortfolioInputDefaultPortfolioType}
                      showPortfolioName={portfolioInputShowPortfolioName}
                      onShowPortfolioNameChange={setPortfolioInputShowPortfolioName}
                      showDateInputs={portfolioInputShowDateInputs}
                      onShowDateInputsChange={setPortfolioInputShowDateInputs}
                    />
                  )}

                  {/* Style/Title/Description Settings for various components.
                  Embed renders its own copy inside its Component Settings tab. */}
                  {hasStyleSettings && componentType !== "embed" && (
                    <StyleConfigForm
                      flowLanguage={flowLanguage}
                      style={componentStyle}
                      onStyleChange={setComponentStyle}
                      title={componentTitle}
                      onTitleChange={setComponentTitle}
                      description={componentDescription}
                      onDescriptionChange={setComponentDescription}
                    />
                  )}

                  {/* Custom Portfolio Fields Settings */}
                  {componentType === "custom_portfolio" && (
                    <CustomPortfolioConfigForm
                      flowLanguage={flowLanguage}
                      showIsin={customPortfolioShowIsin}
                      onShowIsinChange={setCustomPortfolioShowIsin}
                      showProductType={customPortfolioShowProductType}
                      onShowProductTypeChange={setCustomPortfolioShowProductType}
                    />
                  )}

                  {/* Financial Situation Configuration */}
                  {componentType === "financial_situation" && (
                    <FinancialSituationConfigForm
                      flowLanguage={flowLanguage}
                      showIncome={finSitShowIncome}
                      onShowIncomeChange={setFinSitShowIncome}
                      showAssets={finSitShowAssets}
                      onShowAssetsChange={setFinSitShowAssets}
                      showLiabilities={finSitShowLiabilities}
                      onShowLiabilitiesChange={setFinSitShowLiabilities}
                      showCommitments={finSitShowCommitments}
                      onShowCommitmentsChange={setFinSitShowCommitments}
                      showCapacitySection={finSitShowCapacitySection}
                      onShowCapacitySectionChange={setFinSitShowCapacitySection}
                      capacityConfiguredInModel={capacityConfiguredInModel}
                      showStability={finSitShowStability}
                      onShowStabilityChange={setFinSitShowStability}
                      showExpectingChanges={finSitShowExpectingChanges}
                      onShowExpectingChangesChange={setFinSitShowExpectingChanges}
                      showCapacity={finSitShowCapacity}
                      onShowCapacityChange={setFinSitShowCapacity}
                      showSummary={finSitShowSummary}
                      onShowSummaryChange={setFinSitShowSummary}
                    />
                  )}
                  {/* 
                SumSub Verification Configuration
                Liveness verification option only for public pages which end customer sees
              */}
                  {componentType === "sumsub_verification" && componentVisibility === "public" && (
                    <SumSubVerificationConfigForm
                      flowLanguage={flowLanguage}
                      livenessVerification={sumsubLivenessVerification}
                      onLivenessVerificationChange={setSumsubLivenessVerification}
                    />
                  )}

                  {/* Portfolio List payment-detail visibility */}
                  {componentType === "portfolio_list" && (
                    <PortfolioListConfigForm
                      flowLanguage={flowLanguage}
                      showAmount={portfolioListShowAmount}
                      onShowAmountChange={setPortfolioListShowAmount}
                      showFrequency={portfolioListShowFrequency}
                      onShowFrequencyChange={setPortfolioListShowFrequency}
                      showDates={portfolioListShowDates}
                      onShowDatesChange={setPortfolioListShowDates}
                    />
                  )}

                  {/* Widget component info */}
                  {componentType === "risk_assessment" && (
                    <div className="text-sm text-muted-foreground p-3 border rounded-md bg-muted/50">
                      <p className="font-medium mb-1">{t.riskAssessmentRequirements}</p>
                      <p>{t.riskAssessmentNote}</p>
                    </div>
                  )}
                </TabsContent>

                {componentType === "embed" && (
                  <TabsContent value="html" className="space-y-4 mt-4">
                    <EmbedEditor
                      html={embedHtml}
                      flowLanguage={flowLanguage}
                      onChange={setEmbedHtml}
                      frameStyle={componentStyle}
                      title={componentTitle}
                      description={componentDescription}
                    />
                  </TabsContent>
                )}

                {/* Available for every component type */}
                <TabsContent value="conditional" className="space-y-4 mt-4">
                  <ConditionalVisibilityFields
                    enabled={conditionalEnabled}
                    onEnabledChange={setConditionalEnabled}
                    sourceFieldId={sourceFieldId}
                    onSourceFieldIdChange={handleSourceFieldChange}
                    operator={operator}
                    onOperatorChange={setOperator}
                    value={conditionValue}
                    onValueChange={setConditionValue}
                    availableFields={availableFields}
                    clientCustomFields={clientCustomFields}
                    disabled={loading}
                    flowLanguage={flowLanguage}
                    pageType={pageType}
                    portfolioCollectionId={portfolioCollectionId}
                    isSourceExcluded={isConditionSourceExcluded}
                    flowId={flowId}
                  />
                </TabsContent>
              </Tabs>
            </DialogBody>

            <DialogScrollFooter className="sm:justify-between">
              <Button type="button" variant="outline" onClick={handleBack} disabled={loading}>
                {t.back}
              </Button>
              <Button
                type="submit"
                loading={loading}
                disabled={
                  (componentType === "field" &&
                    (filteredFields.length === 0 || !selectedFieldId)) ||
                  (componentType === "quiz" && quizSettings.questions.length === 0)
                }
              >
                {t.addComponentBtn}
              </Button>
            </DialogScrollFooter>
          </form>
        )}
      </DialogScrollContent>
    </Dialog>
  );
}
